import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FaShieldAlt,
  FaFileContract,
  FaGavel,
  FaExclamationTriangle,
  FaUserCheck,
  FaBan,
  FaSyncAlt,
  FaEnvelope,
  FaPhone
} from 'react-icons/fa';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = 'https://api.marketmaestroovip.in';

const TermsConditions = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('acceptance');
     // Scroll to top when page loads
    useEffect(() => {
      window.scrollTo(0, 0);
    }, []);

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        setIsLoading(true);
        const res = await axios.get(`${API_URL}/api/terms`);
        setData(res.data.data || {});
      } catch (err) {
        console.error('Error fetching terms:', err);
        setData({});
      } finally {
        setIsLoading(false);
      }
    };
    fetchTerms();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('[data-section]');
      let current = 'acceptance';
      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 150) {
          current = section.getAttribute('data-section');
        }
      });
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const top =
        element.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const sections = [
    { id: 'acceptance', label: 'Acceptance of Terms', icon: <FaFileContract /> },
    { id: 'eligibility', label: 'Eligibility', icon: <FaUserCheck /> },
    { id: 'services', label: 'Our Services', icon: <FaShieldAlt /> },
    { id: 'sebi', label: 'SEBI Compliance', icon: <FaGavel /> },
    { id: 'risk', label: 'Risk Disclosure', icon: <FaExclamationTriangle /> },
    { id: 'prohibited', label: 'Prohibited Conduct', icon: <FaBan /> },
    { id: 'modifications', label: 'Modifications', icon: <FaSyncAlt /> },
    { id: 'contact', label: 'Contact Us', icon: <FaEnvelope /> }
  ];

  const renderParagraphs = (text) => {
    if (!text) return <p className="text-gray-500 italic">No content added yet.</p>;
    return text.split('\n').filter(Boolean).map((p, i) => (
      <p key={i}>{p}</p>
    ));
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Terms & Conditions</h1>
            <p className="text-blue-100">Loading...</p>
          </div>
        </div>
        <div className="min-h-screen bg-gray-50 py-20">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />

      {/* Hero */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">
            {data.page_title || 'Terms & Conditions'}
          </h1>
          <p className="text-blue-100">{data.page_subtitle || ''}</p>
          <p className="text-blue-200 text-sm mt-1">{data.sebi_line || ''}</p>
        </div>
      </div>

      <section className="min-h-screen py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <aside className="lg:col-span-1">
              <div className="sticky top-24 bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FaFileContract className="text-blue-600" /> Contents
                </h3>
                <nav className="space-y-1">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                        activeSection === section.id
                          ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-blue-600'
                      }`}
                    >
                      <span className="text-xs">{section.icon}</span>
                      {section.label}
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main */}
            <div className="lg:col-span-3 space-y-8">
              {/* Intro */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-500 rounded-lg flex items-center justify-center shadow-lg">
                    <FaFileContract className="text-white text-xl" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      Terms of Service
                    </h2>
                    <p className="text-sm text-gray-500">
                      Last Updated: {data.last_updated || 'January 2026'}
                    </p>
                  </div>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  Welcome to Market Maestroo Private Limited. These Terms & Conditions
                  govern your access to and use of our website, research reports,
                  dashboards, and all related services.
                </p>
              </div>

              {/* Section cards */}
              <SectionCard
                id="acceptance"
                icon={<FaFileContract />}
                title={data.acceptance_title || '1. Acceptance of Terms'}
              >
                {renderParagraphs(data.acceptance_content)}
              </SectionCard>

              <SectionCard
                id="eligibility"
                icon={<FaUserCheck />}
                title={data.eligibility_title || '2. Eligibility'}
              >
                {renderParagraphs(data.eligibility_content)}
              </SectionCard>

              <SectionCard
                id="services"
                icon={<FaShieldAlt />}
                title={data.services_title || '3. Our Services'}
              >
                {renderParagraphs(data.services_content)}
              </SectionCard>

              <SectionCard
                id="sebi"
                icon={<FaGavel />}
                title={data.sebi_title || '4. SEBI Compliance'}
              >
                {renderParagraphs(data.sebi_content)}
              </SectionCard>

              <SectionCard
                id="risk"
                icon={<FaExclamationTriangle />}
                title={data.risk_title || '5. Risk Disclosure'}
              >
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg mb-4">
                  <p className="text-yellow-800 font-semibold">
                    Investments in securities market are subject to market risks.
                    Read all the related documents carefully before investing.
                  </p>
                </div>
                {renderParagraphs(data.risk_content)}
              </SectionCard>

              <SectionCard
                id="prohibited"
                icon={<FaBan />}
                title={data.prohibited_title || '6. Prohibited Conduct'}
              >
                {renderParagraphs(data.prohibited_content)}
              </SectionCard>

              <SectionCard
                id="modifications"
                icon={<FaSyncAlt />}
                title={data.modifications_title || '7. Modifications to Terms'}
              >
                {renderParagraphs(data.modifications_content)}
              </SectionCard>

              <SectionCard
                id="contact"
                icon={<FaEnvelope />}
                title={data.contact_title || '8. Contact Us'}
              >
                {renderParagraphs(data.contact_content)}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                    <FaEnvelope className="text-blue-600 text-xl" />
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <a
                        href={`mailto:${data.contact_email || 'support@marketmaestroo.com'}`}
                        className="text-gray-900 font-semibold hover:text-blue-600"
                      >
                        {data.contact_email || 'support@marketmaestroo.com'}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                    <FaPhone className="text-blue-600 text-xl" />
                    <div>
                      <p className="text-xs text-gray-500">Phone</p>
                      <a
                        href={`tel:${data.contact_phone || '9319745997'}`}
                        className="text-gray-900 font-semibold hover:text-blue-600"
                      >
                        {data.contact_phone || '+91 9319745997'}
                      </a>
                    </div>
                  </div>
                </div>
              </SectionCard>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

const SectionCard = ({ id, icon, title, children }) => (
  <motion.div
    id={id}
    data-section={id}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="bg-white rounded-2xl shadow-lg p-8 scroll-mt-24"
  >
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center shadow-md">
        <span className="text-white">{icon}</span>
      </div>
      <h2 className="text-xl font-bold text-gray-900">{title}</h2>
    </div>
    <div className="text-gray-600 leading-relaxed space-y-3">{children}</div>
  </motion.div>
);

export default TermsConditions;